import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from '@tanstack/react-router';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Campo } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { CamposDeLaFicha } from '../componentes/CamposDeLaFicha';
import { CostoYMargen } from '../componentes/CostoYMargen';
import { ElegirRubros, type Articulo } from './Articulos';
import { type Rubro } from './Rubros';
import { claveTraducida } from './Existencias';
import { importeParaMirar } from '../lib/numero';

interface FilaDeExistencia {
  readonly depositoId: string;
  readonly deposito: string;
  readonly varianteId: string;
  readonly codigo: string;
  readonly cantidad: string;
  readonly valorizacion?: string;
}

interface FilaDePrecio {
  readonly listaId: string;
  readonly lista: string;
  readonly monedaCodigo: string;
  readonly esPredeterminada: boolean;
  readonly varianteId: string;
  readonly variante: string;
  readonly precio: string | null;
  readonly vigenteDesde: string | null;
}

interface Movimiento {
  readonly id: string;
  readonly ocurridoEn: string;
  readonly origen: string;
  readonly motivo: string | null;
  readonly cantidad: string;
  readonly costoUnitario?: string | null;
  readonly deposito: string;
  readonly variante: string;
  readonly observacion: string | null;
}

interface Compra {
  readonly fecha: string;
  readonly numero: string;
  readonly proveedorId: string;
  readonly proveedor: string;
  readonly cantidad: string;
  readonly costoUnitario: string;
  readonly monedaCodigo: string;
}

interface QuienLoLleva {
  readonly terceroId: string;
  readonly tercero: string;
  readonly cantidad: string;
  readonly importe: string;
  readonly ultimaVez: string;
}

/**
 * La ficha de un artículo: qué es y qué le pasó.
 *
 * ## Por qué es una pantalla y no un diálogo
 *
 * Porque todo lo que se sabe de un artículo estaba repartido en cinco pantallas —existencias,
 * precios, costo, movimientos, compras— y para mirarlo había que **saber de antemano en cuál**. Un
 * sistema de gestión contesta «contame todo de esto» en un lugar.
 *
 * ## De dónde salen los datos
 *
 * De cada módulo el suyo: existencias las contesta existencias, los precios los contesta precios.
 * **La pantalla compone; ningún módulo importa a otro** (regla 11). Lo único que no sabe contestar
 * nadie solo —quién lo lleva, a quién se lo compramos— es un informe, y los informes leen de
 * vistas.
 *
 * ## Los bloques que no se pueden ver no se piden
 *
 * Cada tarjeta pregunta por su permiso antes de consultar. Esconder en la pantalla lo que el
 * servidor mandó no es esconderlo, y pedir lo que va a dar 403 llena la consola de errores que no
 * son errores.
 */
export function FichaDeArticulo(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ from: '/articulos/$id' });
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeEditar = usePermiso('CATALOGO.ARTICULO.EDITAR');
  const puedeVerExistencias = usePermiso('EXISTENCIAS.SALDO.VER');
  const puedeVerMovimientos = usePermiso('EXISTENCIAS.KARDEX.VER');
  const puedeVerCosto = usePermiso('EXISTENCIAS.COSTO.VER');
  const puedeVerPrecios = usePermiso('PRECIOS.PRECIO.VER');
  const puedeVerVentas = usePermiso('VENTAS.COMPROBANTE.VER');
  const puedeVerRubros = usePermiso('CATALOGO.CATEGORIA.VER');

  const articulo = useQuery({
    queryKey: ['articulo', id],
    queryFn: () => pedir<Articulo>(`/articulos/${id}`),
  });
  const rubros = useQuery({
    queryKey: ['categorias'],
    enabled: puedeVerRubros,
    queryFn: () => pedir<Rubro[]>('/categorias'),
  });
  const existencias = useQuery({
    queryKey: ['existencias-del-articulo', id],
    enabled: puedeVerExistencias,
    queryFn: () => pedir<FilaDeExistencia[]>(`/existencias?articuloId=${id}`),
  });
  const precios = useQuery({
    queryKey: ['precios-del-articulo', id],
    enabled: puedeVerPrecios,
    queryFn: () => pedir<FilaDePrecio[]>(`/articulos/${id}/precios`),
  });
  const movimientos = useQuery({
    queryKey: ['movimientos-del-articulo', id],
    enabled: puedeVerMovimientos,
    queryFn: () => pedir<Movimiento[]>(`/articulos/${id}/movimientos`),
  });
  const compras = useQuery({
    queryKey: ['compras-del-articulo', id],
    enabled: puedeVerCosto,
    queryFn: () => pedir<Compra[]>(`/informes/articulos/${id}/compras`),
  });
  const quienLoLleva = useQuery({
    queryKey: ['quien-lo-lleva', id],
    enabled: puedeVerVentas,
    queryFn: () => pedir<QuienLoLleva[]>(`/informes/articulos/${id}/quien-lo-lleva`),
  });

  const [edicion, setEdicion] = useState<{
    readonly codigo: string;
    readonly nombre: string;
    readonly marca: string;
    readonly rubros: readonly string[];
    readonly campos: Readonly<Record<string, string>>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // **Lo que se edita se deriva de lo traído hasta que alguien toca algo.** Copiar la ficha al
  // estado en cuanto llega obliga a un efecto, y el efecto pisa lo que la persona venía
  // escribiendo cada vez que la consulta se refresca sola.
  const ficha = articulo.data;
  const enEdicion = edicion ?? {
    codigo: ficha?.codigo ?? '',
    nombre: ficha?.nombre ?? '',
    marca: ficha?.marca ?? '',
    rubros: (ficha?.rubros ?? []).map((rubro) => rubro.id),
    campos: { ...(ficha?.campos ?? {}) },
  };
  const cambiar = (parte: Partial<typeof enEdicion>): void => {
    setEdicion({ ...enEdicion, ...parte });
  };

  const guardar = useMutation({
    mutationFn: () => {
      // **Sólo lo que se tocó.** Mandar la ficha entera escribiría auditoría por campos que nadie
      // cambió, y es la regla de las correcciones desde E15: el antes y el después que queda
      // registrado tiene que ser el de una corrección que de verdad ocurrió.
      const cambios: Record<string, unknown> = {};
      if (ficha !== undefined) {
        if (enEdicion.codigo.trim() !== ficha.codigo) cambios.codigo = enEdicion.codigo.trim();
        if (enEdicion.nombre.trim() !== ficha.nombre) cambios.nombre = enEdicion.nombre.trim();
        if (enEdicion.marca.trim() !== (ficha.marca ?? '')) cambios.marca = enEdicion.marca.trim();

        // Los rubros van enteros o no van: la ruta reemplaza la lista.
        const antes = ficha.rubros.map((rubro) => rubro.id);
        const cambiaron =
          antes.length !== enEdicion.rubros.length ||
          antes.some((rubroId) => !enEdicion.rubros.includes(rubroId));
        if (cambiaron) cambios.rubros = [...enEdicion.rubros];

        const camposTocados = Object.fromEntries(
          Object.entries(enEdicion.campos).filter(
            ([codigo, valor]) => valor !== (ficha.campos[codigo] ?? ''),
          ),
        );
        if (Object.keys(camposTocados).length > 0) cambios.campos = camposTocados;
      }
      return pedir<void>(`/articulos/${id}`, { method: 'PATCH', cuerpo: cambios });
    },
    onSuccess: async () => {
      setError(null);
      setEdicion(null);
      await cola.invalidateQueries({ queryKey: ['articulo', id] });
      await cola.invalidateQueries({ queryKey: ['articulos'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: () =>
      pedir<void>(`/articulos/${id}`, {
        method: 'PATCH',
        cuerpo: { activo: !(ficha?.activo ?? true) },
      }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: ['articulo', id] });
      await cola.invalidateQueries({ queryKey: ['articulos'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  if (articulo.isPending) return <Cargando />;
  if (articulo.isError) {
    return (
      <Problema
        error={articulo.error}
        reintentar={() => {
          void articulo.refetch();
        }}
      />
    );
  }
  if (ficha === undefined) return <Vacio />;

  const columnasDeExistencia: Columna<FilaDeExistencia>[] = [
    { clave: 'deposito', titulo: t('existencias.deposito'), contenido: (fila) => fila.deposito },
    { clave: 'variante', titulo: t('ficha.variante'), contenido: (fila) => fila.codigo },
    {
      clave: 'cantidad',
      titulo: t('existencias.cantidad'),
      numerica: true,
      contenido: (fila) => fila.cantidad,
    },
    ...(puedeVerCosto
      ? [
          {
            clave: 'valorizacion',
            titulo: t('existencias.valorizacion'),
            numerica: true,
            contenido: (fila: FilaDeExistencia) =>
              importeParaMirar(fila.valorizacion ?? '0', i18n.language),
          },
        ]
      : []),
  ];

  return (
    <>
      <Encabezado
        titulo={ficha.nombre}
        descripcion={`${ficha.codigo}${ficha.marca === null ? '' : ` · ${ficha.marca}`}`}
        acciones={
          <>
            <Insignia tono={ficha.activo ? 'bien' : 'neutro'}>
              {ficha.activo ? t('catalogo.activo') : t('catalogo.inactivo')}
            </Insignia>
            <Link to="/articulos">
              <Boton tono="secundario">{t('acciones.volver')}</Boton>
            </Link>
            {puedeEditar ? (
              <Boton
                cargando={cambiarEstado.isPending}
                onClick={() => {
                  cambiarEstado.mutate();
                }}
              >
                {ficha.activo ? t('catalogo.desactivar') : t('catalogo.activar')}
              </Boton>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <Tarjeta
          titulo={t('ficha.datos')}
          descripcion={t('catalogo.corregirAyuda')}
          acciones={
            puedeEditar ? (
              <Boton
                tono="principal"
                cargando={guardar.isPending}
                onClick={() => {
                  guardar.mutate();
                }}
              >
                {t('acciones.guardar')}
              </Boton>
            ) : undefined
          }
        >
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <Campo
                etiqueta={t('catalogo.codigo')}
                value={enEdicion.codigo}
                disabled={!puedeEditar}
                onChange={(evento) => {
                  cambiar({ codigo: evento.target.value });
                }}
              />
              <Campo
                etiqueta={t('catalogo.nombre')}
                value={enEdicion.nombre}
                disabled={!puedeEditar}
                onChange={(evento) => {
                  cambiar({ nombre: evento.target.value });
                }}
              />
              <Campo
                etiqueta={t('catalogo.marca')}
                value={enEdicion.marca}
                disabled={!puedeEditar}
                onChange={(evento) => {
                  cambiar({ marca: evento.target.value });
                }}
              />
            </div>

            {/* La unidad no se edita, y no es un olvido: cambiarla después de que hubo
                movimientos reinterpretaría las cantidades ya registradas —tres kilos pasarían a
                ser tres unidades—. Se muestra para que se sepa cuál es. */}
            <p className="text-apoyo text-texto-tenue">
              {t('ficha.unidadFija', { unidad: ficha.unidad })}
            </p>

            {puedeVerRubros ? (
              <ElegirRubros
                rubros={rubros.data ?? []}
                elegidos={[...enEdicion.rubros]}
                onCambiar={(elegidos) => {
                  cambiar({ rubros: elegidos });
                }}
              />
            ) : null}

            <CamposDeLaFicha
              entidad="articulo"
              valores={enEdicion.campos}
              onCambiar={(codigo, valor) => {
                cambiar({ campos: { ...enEdicion.campos, [codigo]: valor } });
              }}
            />

            {error === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {error}
              </p>
            )}
          </div>
        </Tarjeta>

        <div className="grid gap-4 lg:grid-cols-2">
          {puedeVerExistencias ? (
            <Tarjeta titulo={t('ficha.existencias')}>
              {existencias.isPending ? <Cargando /> : null}
              {existencias.isSuccess ? (
                <Tabla
                  columnas={columnasDeExistencia}
                  filas={existencias.data}
                  claveDeFila={(fila) => `${fila.depositoId}-${fila.varianteId}`}
                  vacio={<Vacio titulo={t('ficha.sinExistencias')} />}
                />
              ) : null}
            </Tarjeta>
          ) : null}

          {puedeVerCosto ? (
            <Tarjeta titulo={t('compras.costoYMargen')} descripcion={t('ficha.porVariante')}>
              {/* Por la primera variante: el costo y el margen son de una variante, no de un
                  artículo. En el caso normal —un artículo sin talles— hay una sola y es ésta. */}
              {ficha.variantes[0] === undefined ? (
                <Vacio />
              ) : (
                <CostoYMargen varianteId={ficha.variantes[0].id} />
              )}
            </Tarjeta>
          ) : null}
        </div>

        {puedeVerPrecios ? (
          <Tarjeta titulo={t('ficha.precios')} descripcion={t('ficha.ayudaPrecios')}>
            {precios.isPending ? <Cargando /> : null}
            {precios.isSuccess ? (
              <Tabla
                columnas={[
                  {
                    clave: 'lista',
                    titulo: t('precios.lista'),
                    contenido: (fila: FilaDePrecio) => (
                      <span>
                        {fila.lista}
                        {fila.esPredeterminada ? (
                          <span className="ml-2 align-middle">
                            <Insignia>{t('precios.esPredeterminada')}</Insignia>
                          </span>
                        ) : null}
                      </span>
                    ),
                  },
                  {
                    clave: 'variante',
                    titulo: t('ficha.variante'),
                    contenido: (fila: FilaDePrecio) => fila.variante,
                  },
                  {
                    clave: 'precio',
                    titulo: t('precios.precio'),
                    numerica: true,
                    // Sin precio se dice, no se deja en blanco: que falte es lo que hay que ver.
                    contenido: (fila: FilaDePrecio) =>
                      fila.precio === null ? (
                        <span className="text-texto-tenue">{t('precios.sinPrecio')}</span>
                      ) : (
                        `${importeParaMirar(fila.precio, i18n.language)} ${fila.monedaCodigo}`
                      ),
                  },
                  {
                    clave: 'desde',
                    titulo: t('precios.desde'),
                    contenido: (fila: FilaDePrecio) => fila.vigenteDesde ?? '',
                  },
                ]}
                filas={precios.data}
                claveDeFila={(fila) => `${fila.listaId}-${fila.varianteId}`}
                vacio={<Vacio titulo={t('ficha.sinPrecios')} />}
              />
            ) : null}
          </Tarjeta>
        ) : null}

        {puedeVerMovimientos ? (
          <Tarjeta titulo={t('ficha.movimientos')} descripcion={t('ficha.ayudaMovimientos')}>
            {movimientos.isPending ? <Cargando /> : null}
            {movimientos.isSuccess ? (
              <Tabla
                columnas={[
                  {
                    clave: 'cuando',
                    titulo: t('existencias.cuando'),
                    contenido: (fila: Movimiento) => new Date(fila.ocurridoEn).toLocaleString(),
                  },
                  {
                    clave: 'deposito',
                    titulo: t('existencias.deposito'),
                    contenido: (fila: Movimiento) => fila.deposito,
                  },
                  {
                    clave: 'origen',
                    titulo: t('existencias.origen'),
                    contenido: (fila: Movimiento) => t(claveTraducida('origen', fila.origen)),
                  },
                  {
                    clave: 'cantidad',
                    titulo: t('existencias.cantidad'),
                    numerica: true,
                    contenido: (fila: Movimiento) => fila.cantidad,
                  },
                ]}
                filas={movimientos.data}
                claveDeFila={(fila) => fila.id}
                vacio={<Vacio titulo={t('ficha.sinMovimientos')} />}
              />
            ) : null}
          </Tarjeta>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          {puedeVerCosto ? (
            <Tarjeta titulo={t('ficha.compras')} descripcion={t('ficha.ayudaCompras')}>
              {compras.isPending ? <Cargando /> : null}
              {compras.isSuccess ? (
                <Tabla
                  columnas={[
                    {
                      clave: 'fecha',
                      titulo: t('compras.fecha'),
                      contenido: (fila: Compra) => fila.fecha,
                    },
                    {
                      clave: 'proveedor',
                      titulo: t('compras.proveedor'),
                      contenido: (fila: Compra) => (
                        <Link
                          to="/proveedores/$id"
                          params={{ id: fila.proveedorId }}
                          className="text-acento underline"
                        >
                          {fila.proveedor}
                        </Link>
                      ),
                    },
                    {
                      clave: 'cantidad',
                      titulo: t('existencias.cantidad'),
                      numerica: true,
                      contenido: (fila: Compra) => fila.cantidad,
                    },
                    {
                      clave: 'costo',
                      titulo: t('compras.costoUnitario'),
                      numerica: true,
                      contenido: (fila: Compra) =>
                        `${importeParaMirar(fila.costoUnitario, i18n.language)} ${fila.monedaCodigo}`,
                    },
                  ]}
                  filas={compras.data}
                  claveDeFila={(fila) => `${fila.numero}-${fila.fecha}-${fila.costoUnitario}`}
                  vacio={<Vacio titulo={t('ficha.sinCompras')} />}
                />
              ) : null}
            </Tarjeta>
          ) : null}

          {puedeVerVentas ? (
            <Tarjeta titulo={t('ficha.quienLoLleva')} descripcion={t('ficha.ayudaQuienLoLleva')}>
              {quienLoLleva.isPending ? <Cargando /> : null}
              {quienLoLleva.isSuccess ? (
                <Tabla
                  columnas={[
                    {
                      clave: 'tercero',
                      titulo: t('clientes.razonSocial'),
                      contenido: (fila: QuienLoLleva) => (
                        <Link
                          to="/clientes/$id"
                          params={{ id: fila.terceroId }}
                          className="text-acento underline"
                        >
                          {fila.tercero}
                        </Link>
                      ),
                    },
                    {
                      clave: 'cantidad',
                      titulo: t('existencias.cantidad'),
                      numerica: true,
                      contenido: (fila: QuienLoLleva) => fila.cantidad,
                    },
                    {
                      clave: 'importe',
                      titulo: t('ficha.importe'),
                      numerica: true,
                      contenido: (fila: QuienLoLleva) =>
                        importeParaMirar(fila.importe, i18n.language),
                    },
                    {
                      clave: 'ultima',
                      titulo: t('ficha.ultimaVez'),
                      contenido: (fila: QuienLoLleva) => fila.ultimaVez,
                    },
                  ]}
                  filas={quienLoLleva.data}
                  claveDeFila={(fila) => fila.terceroId}
                  vacio={<Vacio titulo={t('ficha.sinClientes')} />}
                />
              ) : null}
            </Tarjeta>
          ) : null}
        </div>
      </div>
    </>
  );
}
